/**
 * RelinkDriveFilesButton — restores OVACS's access to files that were stored
 * by a PREVIOUS Google connection (e.g. after the Google Cloud project behind
 * OVACS was recreated).
 *
 * Why this exists: OVACS uses the drive.file scope, so a new OAuth app cannot
 * see files the old one created, even though they still sit untouched in the
 * owner's Drive. Picking a file in the Google Picker grants the current app
 * access to exactly that file ID. The backend then matches the IDs to the
 * existing document records and clears their "missing" flag. Nothing is
 * copied or moved.
 *
 * `parentFolderId` (optional) opens the Picker directly inside the old OVACS
 * workspace folder so the owner can select everything in one go.
 */

import { useState } from 'react'
import { HiOutlineLink } from 'react-icons/hi2'
import { googleDriveAPI } from '../../services/integrations/googleDrive'
import { useToast } from '../common/ToastContainer'
import { loadGooglePicker } from './loadGooglePicker'

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

export default function RelinkDriveFilesButton({
  parentFolderId = null,
  onRelinked,
  className = '',
  disabled = false,
  label = 'Re-link files',
}) {
  const { showSuccess, showError, showWarning } = useToast()
  const [busy, setBusy] = useState(false)

  const unwrap = (res) => (res?.data?.data ?? res?.data ?? null)

  const relink = async (fileIds) => {
    const res = await googleDriveAPI.relink.apply(fileIds)
    if (!res?.success) {
      showError(res?.error?.message || res?.data?.message || 'Re-link failed.', 'Re-link files')
      return
    }
    const payload = unwrap(res) || {}
    const relinked = payload.relinked || []
    const still = payload.still_inaccessible || []
    const foreign = payload.not_in_ovacs || []
    if (relinked.length) {
      showSuccess(`Re-linked ${plural(relinked.length, 'file')}.`, 'Re-link files')
    }
    if (still.length) {
      showWarning(
        `${plural(still.length, 'file')} still can't be opened. Try selecting ${still.length === 1 ? 'it' : 'them'} again.`,
        'Re-link files',
      )
    }
    if (!relinked.length && !still.length && foreign.length) {
      showWarning(
        `The selected ${plural(foreign.length, 'file')} ${foreign.length === 1 ? 'is' : 'are'} not part of this workspace's OVACS records.`,
        'Re-link files',
      )
    }
    onRelinked?.(payload)
  }

  const openPicker = async () => {
    if (busy || disabled) return
    setBusy(true)
    try {
      const res = await googleDriveAPI.pickerConfig()
      if (!res?.success) {
        showWarning(res?.error?.message || 'Re-linking is unavailable right now.', 'Re-link files')
        return
      }
      const cfg = unwrap(res)
      if (!cfg?.access_token || !cfg?.api_key) {
        showWarning('Re-linking is not configured yet.', 'Re-link files')
        return
      }

      await loadGooglePicker()
      const picker = window.google.picker
      const view = new picker.DocsView(picker.ViewId.DOCS)
        .setIncludeFolders(true)
        .setSelectFolderEnabled(false)
      if (parentFolderId) view.setParent(parentFolderId)

      const builder = new picker.PickerBuilder()
        .enableFeature(picker.Feature.MULTISELECT_ENABLED)
        .setTitle('Select the OVACS files to re-link')
        .setOAuthToken(cfg.access_token)
        .setDeveloperKey(cfg.api_key)
        .addView(view)
        .setOrigin(`${window.location.protocol}//${window.location.host}`)
        .setCallback(async (data) => {
          if (data.action !== picker.Action.PICKED) return
          const fileIds = (data.docs || []).map((d) => d.id).filter(Boolean)
          if (!fileIds.length) return
          setBusy(true)
          try {
            await relink(fileIds)
          } catch (e) {
            showError(e?.message || 'Re-link failed.', 'Re-link files')
          } finally {
            setBusy(false)
          }
        })

      if (cfg.app_id) builder.setAppId(String(cfg.app_id))
      builder.build().setVisible(true)
    } catch (e) {
      showError(e?.message || 'Could not open the Google Picker.', 'Re-link files')
    } finally {
      setBusy(false)
    }
  }

  return (
    <button
      type="button"
      onClick={openPicker}
      disabled={busy || disabled}
      className={
        className ||
        'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-warning/40 bg-white text-amber-900 hover:bg-warning-soft disabled:opacity-60 disabled:cursor-not-allowed transition-colors'
      }
      title="Select the files in your previous OVACS folder to restore access"
    >
      <HiOutlineLink className="w-3.5 h-3.5" />
      {busy ? 'Re-linking…' : label}
    </button>
  )
}
